import LaunchIcon from "@mui/icons-material/Launch";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { listProviders, type ProviderCatalogEntry } from "../api/providers";

export function ProvidersPage() {
  const [items, setItems] = useState<ProviderCatalogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listProviders()
      .then((res) => setItems(res.providers))
      .catch((err) => setError(err instanceof Error ? err.message : "Error"))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h5" fontWeight={700}>
          Pasarelas
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Catálogo de pasarelas de pago soportadas. Las marcadas como disponibles son las que los
          comercios pueden elegir para procesar pagos.
        </Typography>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      <Grid container spacing={2}>
        {items.map((p) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={p.id}>
            <Paper variant="outlined" sx={{ p: 2.5, height: "100%" }}>
              <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
                  {p.displayName}
                </Typography>
                <Chip
                  size="small"
                  label={p.available ? "Disponible" : "Próximamente"}
                  color={p.available ? "success" : "default"}
                  variant={p.available ? "filled" : "outlined"}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                ID: <Box component="span" fontFamily="monospace">{p.id}</Box>
              </Typography>
              <Detail label="Países" values={p.countries} />
              <Detail label="Métodos de pago" values={p.paymentMethods} />
              <Detail label="Ambientes" values={p.modes} />
              {p.docsUrl && (
                <Button
                  href={p.docsUrl}
                  target="_blank"
                  rel="noopener"
                  endIcon={<LaunchIcon fontSize="small" />}
                  size="small"
                  sx={{ mt: 1 }}
                >
                  Documentación
                </Button>
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Stack>
  );
}

function Detail({ label, values }: { label: string; values: string[] }) {
  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
        {label}
      </Typography>
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
        {values.length === 0 ? (
          <Typography variant="caption" color="text.disabled">—</Typography>
        ) : (
          values.map((v) => <Chip key={v} size="small" variant="outlined" label={v} />)
        )}
      </Stack>
    </Box>
  );
}
